import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PROJECT_STATUSES, item } from "@/lib/constants";
import { fmtCurrency, fmtDate, minutesToHours, fromNow } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import KanbanBoard from "@/components/KanbanBoard";
import TaskDialog from "@/components/TaskDialog";
import { EmptyState, ListSkeleton } from "@/components/common";
import { Plus, ArrowLeft } from "lucide-react";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const { data: project, isLoading } = useQuery({ queryKey: ["project", id], queryFn: async () => (await api.get(`/projects/${id}`)).data });
  const { data: tasks = [] } = useQuery({ queryKey: ["tasks", "project", id], queryFn: async () => (await api.get("/tasks", { params: { project_id: id } })).data });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/org/members")).data });
  const membersMap = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m.name])), [members]);

  const { data: activity = [] } = useQuery({ queryKey: ["activity"], queryFn: async () => (await api.get("/activity")).data });
  const { data: timeEntries = [] } = useQuery({ queryKey: ["time-entries-all"], queryFn: async () => (await api.get("/time-entries", { params: { mine: false } })).data });

  if (isLoading || !project) return <div><ListSkeleton /></div>;
  const st = item(PROJECT_STATUSES, project.status);
  const done = tasks.filter((t) => t.status === "completed").length;
  const progress = tasks.length ? Math.round(done / tasks.length * 100) : 0;
  const projTime = timeEntries.filter((e) => e.project_id === id).reduce((s, e) => s + (e.minutes || 0), 0);

  const openTask = (tid) => { setEditId(tid); setDialogOpen(true); };
  const addTask = () => { setEditId(null); setDialogOpen(true); };

  return (
    <div>
      <Breadcrumb className="mb-3">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink onClick={() => nav("/projects")} className="cursor-pointer">Projeler</BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbPage>{project.name}</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl grid place-items-center text-white font-bold text-lg font-display" style={{ background: project.color }}>{project.name[0]}</div>
          <div>
            <h1 className="text-2xl font-extrabold font-display">{project.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className={`text-[10px] ${st.chip}`}>{st.label}</Badge>
              <span className="text-xs text-muted-foreground font-mono">{project.code}</span>
            </div>
          </div>
        </div>
        <Button onClick={addTask} data-testid="project-add-task"><Plus className="h-4 w-4 mr-1.5" /> Görev Ekle</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4"><div className="text-xs text-muted-foreground">İlerleme</div><div className="text-2xl font-bold mt-1">{progress}%</div><Progress value={progress} className="h-1.5 mt-2" /></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Görevler</div><div className="text-2xl font-bold mt-1">{done}/{tasks.length}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Bütçe</div><div className="text-2xl font-bold mt-1">{fmtCurrency(project.budget)}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Harcanan zaman</div><div className="text-2xl font-bold mt-1">{minutesToHours(projTime)}</div></Card>
      </div>

      <Tabs defaultValue="kanban">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="proj-tab-overview">Genel Bakış</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="proj-tab-kanban">Kanban</TabsTrigger>
          <TabsTrigger value="list" data-testid="proj-tab-list">Liste</TabsTrigger>
          <TabsTrigger value="time" data-testid="proj-tab-time">Zaman Raporları</TabsTrigger>
          <TabsTrigger value="activity" data-testid="proj-tab-activity">Aktivite</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card className="p-5">
            <h3 className="font-semibold mb-2">Açıklama</h3>
            <p className="text-sm text-muted-foreground">{project.description || "Açıklama yok."}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5 text-sm">
              <div><div className="text-xs text-muted-foreground">Başlangıç</div>{fmtDate(project.start_date)}</div>
              <div><div className="text-xs text-muted-foreground">Bitiş</div>{fmtDate(project.end_date)}</div>
              <div><div className="text-xs text-muted-foreground">Tahmini saat</div>{project.estimated_hours}s</div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="kanban" className="mt-4">
          {tasks.length ? <KanbanBoard tasks={tasks} onOpen={openTask} onAdd={addTask} membersMap={membersMap} /> : <EmptyState title="Görev yok" action={<Button onClick={addTask}><Plus className="h-4 w-4 mr-1.5" /> Görev Ekle</Button>} />}
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <Card className="divide-y">
            {tasks.map((t) => (
              <button key={t.id} onClick={() => openTask(t.id)} className="w-full flex items-center gap-3 p-3 hover:bg-muted text-left">
                <span className="text-xs font-mono text-muted-foreground">#{t.number}</span>
                <span className="text-sm flex-1 truncate">{t.title}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(t.due_date)}</span>
              </button>
            ))}
            {tasks.length === 0 && <EmptyState title="Görev yok" />}
          </Card>
        </TabsContent>

        <TabsContent value="time" className="mt-4">
          <Card className="divide-y">
            {timeEntries.filter((e) => e.project_id === id).map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-3">
                <span className="text-sm flex-1">{e.description || "Zaman kaydı"} <span className="text-muted-foreground">— {e.user_name}</span></span>
                <span className="text-sm font-medium">{minutesToHours(e.minutes)}</span>
              </div>
            ))}
            {timeEntries.filter((e) => e.project_id === id).length === 0 && <EmptyState title="Zaman kaydı yok" />}
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card className="divide-y">
            {activity.filter((a) => a.entity_type === "project" && a.entity_id === id || a.entity_type === "task").slice(0, 20).map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 text-sm">
                <span className="font-medium">{a.user_name}</span>
                <span className="text-muted-foreground">{a.action === "created" ? "oluşturdu" : a.action === "status_changed" ? "durumu değiştirdi" : a.action}</span>
                <span className="truncate">{a.detail}</span>
                <span className="ml-auto text-xs text-muted-foreground">{fromNow(a.created_at)}</span>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>

      <TaskDialog open={dialogOpen} onOpenChange={(v) => { if (!v) { setDialogOpen(false); setEditId(null); } }} taskId={editId} defaultProject={id} key={editId || "new"} />
    </div>
  );
}
